// Port of Flutter's loginScreen.dart.
//
// Layout, wording and validation order all match the Dart: mobile (10 digits) +
// 4-digit PIN over the frosted card, a Terms checkbox that must be ticked before
// the button does anything, then Forgot Password and Sign Up links.
//
// The device-move OTP dialog is the Dart `Checker()` — shown when the account is
// registered to a different install; entering the code re-binds it here.

import React, { useState } from 'react';
import {
  Box, Typography, Checkbox, Link, IconButton, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, useTheme,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import PasswordIcon from '@mui/icons-material/Password';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import GlassCard from '../components/GlassCard';
import AuthField from '../components/AuthField';
import AuthButton from '../components/AuthButton';
import { useMessage } from '../components/MessageDialog';
import { useApp } from '../../../core/state/AppContext';
import { attemptLogin, confirmDeviceBinding } from '../../../core/auth/loginFlow';
import { isTestMobile } from '../../../core/auth/testAccount';
import { PRIVACY_POLICY_URL, WHATSAPP_URL, openExternal } from '../../../core/constants';

const HEADING_FONT = "'BetterFonts', 'Satisfy', cursive";

export default function MobileLoginScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { setSession } = useApp();
  const { show } = useMessage();

  const [mobile, setMobile] = useState('');
  const [pin, setPin] = useState('');
  const [hidePin, setHidePin] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  // Device-move OTP dialog state.
  const [otpPrompt, setOtpPrompt] = useState(null); // { otp, userData }
  const [otpInput, setOtpInput] = useState('');
  const [otpError, setOtpError] = useState('');

  const submit = async () => {
    if (!agreed) {
      show('Note', 'You need to agree to terms and conditions before using the app.');
      return;
    }
    if (mobile.length !== 10) {
      show('Invalid Number', 'Number must be 10 digits long.');
      return;
    }
    // The review test number has no password to be empty — see
    // core/auth/testAccount.js. Blocking here would defeat the exemption
    // before attemptLogin ever saw the number.
    if (!pin && !isTestMobile(mobile)) {
      show('Invalid Password', 'Password cannot be empty.');
      return;
    }

    setBusy(true);
    const result = await attemptLogin(mobile, pin);
    setBusy(false);

    if (result.status === 'invalid') {
      show('Invalid Credentials',
        'Please Check the entered credentials as they are found invalid on server.');
      return;
    }
    if (result.status === 'error') {
      show('Login Failed',
        'Something went wrong while logging in.\n\nPlease check your connection and try again.');
      return;
    }
    if (result.status === 'otp') {
      setOtpInput('');
      setOtpError('');
      setOtpPrompt(result);
      return;
    }
    setSession(result.userData);
    navigate('/home', { replace: true });
  };

  const submitOtp = async () => {
    if (!otpPrompt) return;
    if (otpInput.trim() !== String(otpPrompt.otp ?? '')) {
      setOtpError('Incorrect OTP. Please try again.');
      return;
    }
    setBusy(true);
    const fresh = await confirmDeviceBinding(otpPrompt.userData, mobile, pin);
    setBusy(false);
    setOtpPrompt(null);
    setSession(fresh);
    navigate('/home', { replace: true });
  };

  return (
    <GlassCard>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 32, color: c.text, fontWeight: 700, fontFamily: HEADING_FONT }}>
          Login
        </Typography>

        <Box sx={{ height: 20 }} />
        {/* `numeric` rather than a bare inputMode prop, and the PIN is masked
            with `mask` rather than type="password" — see AuthField for why both
            of the obvious spellings silently gave an alphanumeric keyboard.
            The 10-digit cap is enforced twice: maxLength stops the typing, and
            the onChange strips anything that is not a digit, which is what
            catches a paste. */}
        <AuthField
          placeholder="Mobile Number"
          icon={<PhoneIcon />}
          numeric
          maxLength={10}
          value={mobile}
          onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
        />

        <Box sx={{ height: 20 }} />
        <AuthField
          placeholder="Pin"
          icon={<PasswordIcon />}
          numeric
          maxLength={4}
          mask={hidePin}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
          endAdornment={(
            <InputAdornment position="end">
              <IconButton onClick={() => setHidePin((v) => !v)} sx={{ color: c.text }}>
                <VisibilityIcon />
              </IconButton>
            </InputAdornment>
          )}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', py: 1.25 }}>
          <Checkbox
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            sx={{
              color: '#BDBDBD',
              '&.Mui-checked': { color: '#000' },
            }}
          />
          <Typography sx={{ color: c.text, fontSize: 16 }}>
            I agree to the{' '}
            <Link
              component="button"
              type="button"
              onClick={() => openExternal(PRIVACY_POLICY_URL)}
              sx={{ color: c.text, fontSize: 16, textDecorationColor: c.text }}
            >
              Terms And Conditions
            </Link>
          </Typography>
        </Box>

        <AuthButton onClick={submit} disabled={busy}>Login</AuthButton>

        <Box sx={{ height: 20 }} />
        <Typography sx={{ color: c.text, fontSize: 16, textAlign: 'center' }}>
          <Link
            component="button"
            type="button"
            onClick={() => navigate('/forgot')}
            sx={{ color: c.text, fontSize: 16, textDecorationColor: c.text }}
          >
            Forgot Password?
          </Link>
          {' Recover in seconds.'}
        </Typography>

        <Box sx={{ height: 20 }} />
        <Typography sx={{ color: c.text, fontSize: 16, textAlign: 'center' }}>
          {"Don't have an account? "}
          <Link
            component="button"
            type="button"
            onClick={() => navigate('/signup')}
            sx={{ color: c.text, fontSize: 16, textDecorationColor: c.text }}
          >
            Sign Up
          </Link>
        </Typography>
      </Box>

      {/* Device-move OTP — Flutter's Checker() dialog. */}
      <Dialog open={!!otpPrompt} onClose={() => setOtpPrompt(null)}
        PaperProps={{ sx: { borderRadius: '15px' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Enter OTP from WhatsApp</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 20, color: 'grey.600', mb: 1.25 }}>
            Logging in here will logout your account from other devices.
          </Typography>
          {/* The OTP is digits too, and this had the same dropped-inputMode
              bug — on a plain TextField it must go inside inputProps. */}
          <TextField
            autoFocus
            fullWidth
            placeholder="Type OTP..."
            value={otpInput}
            onChange={(e) => { setOtpInput(e.target.value.replace(/\D/g, '')); setOtpError(''); }}
            error={!!otpError}
            helperText={otpError || ' '}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', autoComplete: 'one-time-code' }}
          />
          <Typography sx={{ fontSize: 20, fontWeight: 700, p: 1 }}>
            {'Get OTP from '}
            <Link
              component="button"
              type="button"
              onClick={() => openExternal(WHATSAPP_URL)}
              sx={{ fontSize: 20 }}
            >
              WhatsApp
            </Link>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOtpPrompt(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitOtp} disabled={busy}>Submit</Button>
        </DialogActions>
      </Dialog>
    </GlassCard>
  );
}
